import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ChevronDown, Menu, X } from "lucide-react";
import { TEL_LINK, waLink, PHONE_DISPLAY } from "@/config/contact";

const DIENSTEN = [
  { to: "/lekdetectie", label: "Lekdetectie" },
  { to: "/daklekkage", label: "Daklekkage" },
  { to: "/leidinglekkage", label: "Leidinglekkage" },
  { to: "/waterschade", label: "Waterschade herstel" },
  { to: "/lekkage-vve", label: "Lekkage bij VvE's" },
];
const WERKGEBIED = [
  { to: "/lekkage-amsterdam", label: "Amsterdam" },
  { to: "/lekkage-utrecht", label: "Utrecht" },
  { to: "/lekkage-almere", label: "Almere" },
  { to: "/lekkage-amstelveen", label: "Amstelveen" },
  { to: "/lekkage-zaandam", label: "Zaandam" },
  { to: "/lekkage-amersfoort", label: "Amersfoort" },
  { to: "/lekkage-hilversum", label: "Hilversum" },
  { to: "/lekkage-haarlem", label: "Haarlem" },
];

const Dropdown = ({ label, items, testid }) => (
  <div className="relative group" data-testid={testid}>
    <button type="button" className="flex items-center gap-1.5 text-sm font-bold text-gray-200 hover:text-[#20C7C9] transition-colors py-2" data-testid={`${testid}-trigger`}>
      {label} <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block">
      <div className="bg-[#111C2C] border border-white/10 rounded-xl shadow-2xl py-2 min-w-56">
        {items.map((item) => (
          <Link key={item.to} to={item.to} data-testid={`${testid}-link-${item.to.slice(1)}`} className="block px-5 py-2.5 text-sm font-bold text-gray-200 hover:text-[#20C7C9] hover:bg-white/5 transition-colors">
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      data-cta-location="header"
      className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#0B1320]/70 border-b border-white/10"
    >
      <div className="h-14 md:h-20 flex items-center justify-between gap-3 px-3 sm:px-6 lg:px-8 max-w-[1600px] mx-auto">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0 min-w-0 py-2 -my-2 md:py-0 md:my-0" aria-label="Spoedlekkage.nl — naar homepage" onClick={() => setOpen(false)}>
          <img src="/logo-icon-64.webp" alt="Spoedlekkage.nl" width="64" height="66" className="h-7 md:h-8 w-auto shrink-0" />
          <span className="font-display font-black text-[13px] sm:text-base md:text-xl tracking-tighter uppercase whitespace-nowrap">
            Spoed<span className="text-[#20C7C9]">lekkage</span>.nl
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Hoofdmenu">
          <Dropdown label="Diensten" items={DIENSTEN} testid="nav-diensten" />
          <Dropdown label="Werkgebied" items={WERKGEBIED} testid="nav-werkgebied" />
          <Link to="/lekkage-verzekering" data-testid="nav-verzekering" className="text-sm font-bold text-gray-200 hover:text-[#20C7C9] transition-colors">Verzekering</Link>
          <Link to="/kennisbank" data-testid="nav-kennisbank" className="text-sm font-bold text-gray-200 hover:text-[#20C7C9] transition-colors">Kennisbank</Link>
        </nav>

        <div className="hidden md:flex items-center gap-6 shrink-0">
          <a href={TEL_LINK} data-testid="header-call-link" className="js-phone group text-right">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-gray-400">Bel ons</span>
            <span className="font-display font-black text-xl lg:text-2xl tracking-tight text-white group-hover:text-[#20C7C9] transition-colors duration-300">
              {PHONE_DISPLAY}
            </span>
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="bg-white text-black hover:bg-gray-200 rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp
          </a>
        </div>

        {/* Mobiel: drie ronde knoppen van 44px — bellen, WhatsApp, menu */}
        <div className="flex md:hidden items-center gap-2 shrink-0">
          <a href={TEL_LINK} data-testid="header-call-mobile" aria-label={`Bel ${PHONE_DISPLAY}`} className="js-phone w-11 h-11 rounded-full bg-[#20C7C9] text-black flex items-center justify-center active:scale-95 transition-transform">
            <Phone className="w-5 h-5" />
          </a>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-mobile" aria-label="WhatsApp ons" className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center active:scale-95 transition-transform">
            <MessageCircle className="w-5 h-5" />
          </a>
          <button type="button" onClick={() => setOpen(!open)} data-testid="header-menu-button" aria-label={open ? "Menu sluiten" : "Menu openen"} aria-expanded={open} className="w-11 h-11 rounded-full border border-white/15 text-white flex items-center justify-center active:scale-95 transition-transform">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-white/10 bg-[#0B1320] px-4 sm:px-6 pt-4 pb-6 max-h-[calc(100vh-3.5rem)] overflow-y-auto" aria-label="Mobiel menu" data-testid="mobile-menu">
          <p className="text-[13px] font-bold tracking-[0.2em] uppercase text-gray-500">Diensten</p>
          <div className="flex flex-col">
            {DIENSTEN.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${item.to.slice(1)}`} className="flex items-center min-h-12 font-display font-bold text-lg text-white hover:text-[#20C7C9] transition-colors border-b border-white/5">
                {item.label}
              </Link>
            ))}
          </div>
          <p className="mt-5 text-[13px] font-bold tracking-[0.2em] uppercase text-gray-500">Werkgebied</p>
          <div className="flex flex-col">
            {WERKGEBIED.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${item.to.slice(1)}`} className="flex items-center min-h-12 font-display font-bold text-lg text-white hover:text-[#20C7C9] transition-colors border-b border-white/5">
                Lekkage {item.label}
              </Link>
            ))}
          </div>
          <div className="flex flex-col">
            <Link to="/lekkage-verzekering" onClick={() => setOpen(false)} data-testid="mobile-nav-verzekering" className="flex items-center min-h-12 font-display font-bold text-lg text-white hover:text-[#20C7C9] transition-colors border-b border-white/5">Verzekering</Link>
            <Link to="/kennisbank" onClick={() => setOpen(false)} data-testid="mobile-nav-kennisbank" className="flex items-center min-h-12 font-display font-bold text-lg text-white hover:text-[#20C7C9] transition-colors border-b border-white/5">Kennisbank</Link>
            <Link to="/over-ons" onClick={() => setOpen(false)} data-testid="mobile-nav-over-ons" className="flex items-center min-h-12 font-display font-bold text-lg text-white hover:text-[#20C7C9] transition-colors">Over ons</Link>
          </div>
          <a href={TEL_LINK} onClick={() => setOpen(false)} data-testid="mobile-nav-call-button" className="js-phone mt-6 w-full h-12 rounded-full bg-[#20C7C9] text-black font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform">
            <Phone className="w-5 h-5" /> Bel {PHONE_DISPLAY}
          </a>
        </nav>
      )}
    </header>
  );
}
